import {combineReducers} from 'redux';
import {reducer as reduxFormReducer} from 'redux-form';

import {type Action} from 'web-app/util/redux/action-types';
import importModalReducer, {type ImportModalState} from 'web-app/react/import/import-modal/reducer';
import famlyPayPaymentSetup, {
    type FamlyPayPaymentSetupFeedItemState,
} from 'web-app/react/feed/post/embeds/payment-setup/reducer';
import groupHeader, {type GroupHeaderState} from 'web-app/react/institution/group-header/reducer';
import groupTagChart, {type GroupTagChartState} from 'web-app/react/institution/group-tag-chart/reducer';
import lists, {type ListsState} from 'web-app/react/lists/reducer';
import {type InferReducerType} from 'web-app/util/redux';
import billingOverview, {type BillingOverviewState} from 'web-app/react/billing-overview/reducer';
import famlyLive, {type FamlyLiveState} from 'web-app/react/famly-live/reducer';
import lessonPlanSectionModal, {
    type LessonPlanSectionModalState,
} from 'web-app/react/lesson-plans/lesson-plan-section-modal/reducer';
import lessonPlans, {type LessonPlansState} from 'web-app/react/lesson-plans/reducer';
import headCountModal, {type HeadCountModalState} from 'web-app/react/health-and-safeguarding/headcount/modal/reducer';
import parentalPermissionsMatrix, {
    type ParentalPermissionsMatrixState,
} from 'web-app/react/parental-permissions/matrix/reducer';

import accountSettings, {type AccountSettingsState} from '../account-settings/reducer';
import balances from '../balances/reducer';
import calendar from '../calendar/reducer';
import changeRoleModal from '../change-role-modal/reducer';
import childDevelopmentOverview from '../child-development/child-development-overview/reducer';
import closingDays from '../closing-days/reducer';
import asyncButton, {type AsyncButtonState} from '../components/async-link-button/reducer';
import billPayerForm from '../components/bill-payer-form/reducer';
import fileUpload from '../components/file-upload/reducer';
import helpIcon from '../components/help-icon/reducer';
import documents, {type DocumentsDataState} from '../documents/reducer';
import entities, {type EntitiesState} from '../entities/reducer';
import famlyReport from '../famly-report/reducer';
import feed from '../feed/reducer';
import imageGallery from '../image-gallery/reducer';
import inbox from '../inbox/reducer';
import inquiries from '../inquiries/reducer';
import institutionSettings, {type InstitutionSettingsState} from '../institution/settings/reducer';
import mainNavigation, {type MainNavigationState} from '../main-navigation/reducer';
import manageEmployeeRights, {type ManageEmployeeRightsState} from '../manage-employee-rights/reducer';
import meals from '../meals/reducer';
import modals, {type ModalsState} from '../modals-container/reducer';
import notifications, {type NotificationState} from '../notifications/reducer';
import organizationDashboard from '../organization/organization-overview/dashboard/reducer';
import organization from '../organization/reducer';
import parentalPermissions from '../parental-permissions/overview/reducer';
import invoiceSettings from '../plans/invoice-settings/reducer';
import plans from '../plans/reducer';
import newEmployee, {type NewEmployeeState} from '../profile/new-employee/reducer';
import billPayerProfileV2, {
    type BillPayerProfileState as BillPayerProfileStateV2,
} from '../profile/bill-payer-v2/reducer';
import childProfile from '../profile/child-profile/reducer';
import employeeProfile, {type EmployeeProfileState} from '../profile/employee-profile/reducer';
import roomPlanning from '../room-planning/reducer';
import session from '../session/reducer';
import topBar, {type TopBarState} from '../top-bar/reducer';
import institutionPeople from '../institution-people/reducer';
import contract from '../routes/contract/reducer';
import activities from '../activities/reducer';
import quickSearch, {type QuickSearchState} from '../quick-search/reducer';
import globalEvent from '../global-event/reducer';
import {type GlobalEventState} from '../global-event/helpers/create-reducer';
import invoicingProfiles, {type InvoicingProfileState} from '../invoicing-profiles/reducer';
import inquiryForm, {type InquiryFormState} from '../routes/inquiry-form/reducer';
import manageChildren, {type ManageChildrenState} from '../manage-children/reducer';
import inviteParents, {type InviteParentsState} from '../invite-parents/reducer';
import safeguarding, {type SafeguardingState} from '../health-and-safeguarding/accidents-incidents/reducer';
import abstractBudget, {type AbstractBudgetState} from '../abstract-bugdet/reducer';
import login, {type LoginState} from '../login/reducer';
import cohortCompare, {type CohortCompareState} from '../child-development/cohort-compare/reducer';
import advancedPermissions, {type PermissionsState} from '../manage-employees/permissions/reducer';
import customerSatisfaction, {type CustomerSatisfactionState} from '../customer-satisfaction/reducer';
import twoFactorSettings, {type TwoFactorSettingsState} from '../account-settings/two-factor-settings/reducer';
import leaveWidget from '../profile/leave-widget/reducer';
import manageOrganizationEmployees, {
    type ManageOrganizationEmployeesState,
} from '../organization/manage-employees/reducer';
import vacationPlanning, {type VacationPlanningState} from '../vacation-planning/reducer';
import homePage, {type HomepageState} from '../homepage/reducer';
import peoplePicker, {type PeoplePickerState} from '../people-picker/reducer';
import institutionOverview, {type InstitutionOverviewState} from '../institution/overview/reducer';
import newGroup, {type NewGroupState} from '../institution/new-group/reducer';
import newTag, {type NewTagState} from '../institution/new-tag/reducer';
import importState, {type ImportState} from '../import/reducer';
import signInOut, {type SignInOutState} from '../settings/content/app-settings/sign-in-out/reducer';
import survey, {type SurveyState} from '../routes/account/survey/reducer';
import newDepartment, {type NewDepartmentState} from '../institution/new-department/reducer';
import newEmployeeContact, {type NewEmployeeContactState} from '../profile/employee-profile/new-contact/reducer';
import {type InquiriesState} from '../inquiries/reducer';
import confirmEmail, {type ConfirmEmailState} from '../routes/confirm-email/reducer';
import dialog, {type DialogState} from '../dialog/reducer';
import navigationV2, {type NavigationV2State} from '../navigation-v2/reducer';
import firstPaymentMethodBanner, {type FirstPaymentMethodBannerState} from '../feed/feed-banners/reducer';

/*
 * This is a redux anti-pattern, however, we need to do this in order to listen for specific
 * actions inside of our Ember components.
 *
 * Store subscribers are not guaranteed to get all stage changes, so for lastAction we filter out non-Famly
 * actions in order to reduce the risk of missing an action.
 */
const lastAction = (state = {}, action: any) => (action.type.startsWith('@') ? state : action);
const anyLastAction = (state: RootState, action: any) => action || {};

// Please order additions alphabetically. Makes it a lot easier to navigate the state in redux dev tools
export const reducersObj = {
    abstractBudget,
    accountSettings,
    activities,
    advancedPermissions,
    anyLastAction,
    asyncButton,
    balances,
    billPayerForm,
    billPayerProfileV2,
    billingOverview,
    calendar,
    changeRoleModal,
    signInOut,
    childDevelopmentOverview,
    childProfile,
    closingDays,
    cohortCompare,
    confirmEmail,
    contract,
    customerSatisfaction,
    dialog,
    documents,
    employeeProfile,
    entities,
    famlyLive,
    famlyPayPaymentSetup,
    famlyReport,
    feed,
    fileUpload,
    form: reduxFormReducer,
    globalEvent,
    groupHeader,
    groupTagChart,
    headCountModal,
    helpIcon,
    homePage,
    imageGallery,
    import: importState,
    importModal: importModalReducer,
    inbox,
    inquiries,
    inquiryForm,
    institutionOverview,
    institutionPeople,
    institutionSettings,
    inviteParents,
    invoiceSettings,
    invoicingProfiles,
    lastAction,
    leaveWidget,
    lessonPlanSectionModal,
    lessonPlans,
    lists,
    login,
    mainNavigation,
    manageChildren,
    manageEmployeeRights,
    manageOrganizationEmployees,
    meals,
    modals,
    navigationV2,
    newDepartment,
    newEmployee,
    newEmployeeContact,
    newGroup,
    newTag,
    notifications,
    organization,
    organizationDashboard,
    parentalPermissions,
    parentalPermissionsMatrix,
    peoplePicker,
    plans,
    firstPaymentMethodBanner,
    quickSearch,
    roomPlanning,
    safeguarding,
    session,
    survey,
    topBar,
    twoFactorSettings,
    vacationPlanning,
};

export interface RootState {
    newEmployee: NewEmployeeState;
    topBar: TopBarState;
    closingDays: any;
    changeRoleModal: any;
    childProfile: InferReducerType<typeof childProfile>;
    balances: any;
    billPayerProfileV2: BillPayerProfileStateV2;
    documents: DocumentsDataState;
    employeeProfile: EmployeeProfileState;
    entities: EntitiesState;
    notifications: NotificationState;
    feed: any;
    form: any;
    billPayerForm: any;
    fileUpload: any;
    imageGallery: any;
    lastAction: any;
    anyLastAction: any;
    mainNavigation: MainNavigationState;
    manageEmployeeRights: ManageEmployeeRightsState;
    meals: any;
    modals: ModalsState;
    plans: InferReducerType<typeof plans>;
    inquiries: InquiriesState;
    roomPlanning: any;
    invoiceSettings: any;
    organization: any;
    session: any;
    calendar: any;

    institutionSettings: InstitutionSettingsState;
    famlyReport: any;
    inbox: any;
    childDevelopmentOverview: any;
    helpIcon: any;
    accountSettings: AccountSettingsState;
    organizationDashboard: any;
    asyncButton: AsyncButtonState;
    parentalPermissions: any;
    institutionPeople: any;
    activities: any;
    quickSearch: QuickSearchState;
    invoicingProfiles: InvoicingProfileState;
    inquiryForm: InquiryFormState;
    manageChildren: ManageChildrenState;
    inviteParents: InviteParentsState;
    globalEvent: GlobalEventState;
    login: LoginState;
    safeguarding: SafeguardingState;
    abstractBudget: AbstractBudgetState;
    cohortCompare: CohortCompareState;
    advancedPermissions: PermissionsState;
    customerSatisfaction: CustomerSatisfactionState;
    leaveWidget: any;
    manageOrganizationEmployees: ManageOrganizationEmployeesState;
    vacationPlanning: VacationPlanningState;
    twoFactorSettings: TwoFactorSettingsState;
    homePage: HomepageState;
    peoplePicker: PeoplePickerState;
    institutionOverview: InstitutionOverviewState;
    newGroup: NewGroupState;
    newTag: NewTagState;
    import: ImportState;
    importModal: ImportModalState;
    signInOut: SignInOutState;
    newDepartment: NewDepartmentState;
    newEmployeeContact: NewEmployeeContactState;
    confirmEmail: ConfirmEmailState;
    dialog: DialogState;
    famlyPayPaymentSetup: FamlyPayPaymentSetupFeedItemState;
    groupHeader: GroupHeaderState;
    groupTagChart: GroupTagChartState;
    lists: ListsState;
    billingOverview: BillingOverviewState;
    famlyLive: FamlyLiveState;
    lessonPlanSectionModal: LessonPlanSectionModalState;
    lessonPlans: LessonPlansState;
    headCountModal: HeadCountModalState;
    parentalPermissionsMatrix: ParentalPermissionsMatrixState;
    navigationV2: NavigationV2State;
    survey: SurveyState;
    firstPaymentMethodBanner: FirstPaymentMethodBannerState;
}

export default combineReducers<RootState, Action<any>>(reducersObj);
